import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  RefreshCw,
  RotateCcw,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  WhatsAppBotReadinessCheck,
  WhatsAppBotReadinessStatusResponse,
  WhatsAppFlowOperationsStatus,
  WhatsAppSystemFlowStatusItem,
} from "@/types/communication-integration";
import {
  useSyncWhatsAppSystemFlows,
  useWhatsAppBotReadinessStatus,
  useWhatsAppFlowOperationsStatus,
} from "../hooks/whatsapp-provisioning.hooks";

interface WhatsappFlowOperationsPanelProps {
  hotelId: string;
}

export const WhatsappFlowOperationsPanel: React.FC<
  WhatsappFlowOperationsPanelProps
> = ({ hotelId }) => {
  const { t } = useTranslation();
  const [recreateOpen, setRecreateOpen] = React.useState(false);
  const statusQuery = useWhatsAppFlowOperationsStatus(hotelId, true);
  const readinessQuery = useWhatsAppBotReadinessStatus(hotelId, true);
  const syncFlows = useSyncWhatsAppSystemFlows(hotelId);
  const isBusy =
    statusQuery.isFetching || readinessQuery.isFetching || syncFlows.isPending;

  const handleSync = (replaceExisting: boolean, onSuccess?: () => void) => {
    syncFlows.mutate(
      { replaceExisting },
      {
        onSuccess: (result) => {
          onSuccess?.();
          toast.success(
            t("communicationChannels.whatsapp.flowOps.syncSuccess", {
              created: result.systemFlows.created,
              skipped: result.systemFlows.skipped,
            }),
          );
        },
        onError: () => {
          toast.error(t("communicationChannels.whatsapp.flowOps.syncError"));
        },
      },
    );
  };

  return (
    <section className="flex flex-col gap-4 border-t pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h4 className="text-sm font-medium text-foreground">
            {t("communicationChannels.whatsapp.flowOps.title")}
          </h4>
          <p className="text-xs text-muted-foreground">
            {t("communicationChannels.whatsapp.flowOps.description")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="new_outline"
            size="sm"
            disabled={isBusy}
            onClick={() => {
              statusQuery.refetch();
              readinessQuery.refetch();
            }}
          >
            <RefreshCw
              className={
                statusQuery.isFetching ? "size-4 animate-spin" : "size-4"
              }
            />
            {t("communicationChannels.whatsapp.flowOps.actions.refresh")}
          </Button>
          <Button
            type="button"
            variant="new_outline"
            size="sm"
            disabled={isBusy}
            onClick={() => handleSync(false)}
          >
            {syncFlows.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
            {t("communicationChannels.whatsapp.flowOps.actions.sync")}
          </Button>
          <Button
            type="button"
            variant="new_secondary"
            size="sm"
            disabled={isBusy}
            onClick={() => setRecreateOpen(true)}
          >
            {syncFlows.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RotateCcw className="size-4" />
            )}
            {t("communicationChannels.whatsapp.flowOps.actions.recreate")}
          </Button>
        </div>
      </div>

      {renderReadinessBody(readinessQuery, t)}
      {renderStatusBody(statusQuery, t)}

      <Dialog open={recreateOpen} onOpenChange={setRecreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t(
                "communicationChannels.whatsapp.flowOps.recreateConfirm.title",
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                "communicationChannels.whatsapp.flowOps.recreateConfirm.description",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="new_outline"
              disabled={syncFlows.isPending}
              onClick={() => setRecreateOpen(false)}
            >
              {t("communicationChannels.actions.cancel")}
            </Button>
            <Button
              type="button"
              variant="new_default"
              disabled={syncFlows.isPending}
              onClick={() =>
                handleSync(true, () => {
                  setRecreateOpen(false);
                })
              }
            >
              {syncFlows.isPending && (
                <Loader2 className="size-4 animate-spin" />
              )}
              {t(
                "communicationChannels.whatsapp.flowOps.recreateConfirm.confirm",
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

const renderReadinessBody = (
  readinessQuery: ReturnType<typeof useWhatsAppBotReadinessStatus>,
  t: (key: string, options?: Record<string, unknown>) => string,
): React.ReactNode => {
  if (readinessQuery.isLoading) {
    return (
      <p className="text-xs text-muted-foreground">
        {t("communicationChannels.whatsapp.flowOps.readiness.loading")}
      </p>
    );
  }

  if (readinessQuery.isError || !readinessQuery.data) {
    return (
      <p className="text-xs text-destructive">
        {t("communicationChannels.whatsapp.flowOps.readiness.loadError")}
      </p>
    );
  }

  return <ReadinessStatusContent data={readinessQuery.data} t={t} />;
};

const renderStatusBody = (
  statusQuery: ReturnType<typeof useWhatsAppFlowOperationsStatus>,
  t: (key: string, options?: Record<string, unknown>) => string,
): React.ReactNode => {
  if (statusQuery.isLoading) {
    return (
      <p className="text-xs text-muted-foreground">
        {t("communicationChannels.whatsapp.flowOps.loading")}
      </p>
    );
  }

  if (statusQuery.isError || !statusQuery.data) {
    return (
      <p className="text-xs text-destructive">
        {t("communicationChannels.whatsapp.flowOps.loadError")}
      </p>
    );
  }

  return <FlowStatusContent data={statusQuery.data} t={t} />;
};

interface ReadinessStatusContentProps {
  data: WhatsAppBotReadinessStatusResponse;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const ReadinessStatusContent: React.FC<ReadinessStatusContentProps> = ({
  data,
  t,
}) => (
  <div className="flex flex-col gap-3 rounded-md border bg-background p-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("communicationChannels.whatsapp.flowOps.readiness.title")}
        </span>
        <span className="text-sm font-medium text-foreground">
          {t(
            `communicationChannels.whatsapp.flowOps.readiness.status.${data.status}`,
          )}
        </span>
      </div>
      <Badge variant={readinessStatusVariant(data.status)}>
        {t(
          `communicationChannels.whatsapp.flowOps.readiness.status.${data.status}`,
        )}
      </Badge>
    </div>

    {(data.blockingIssues.length > 0 || data.warnings.length > 0) && (
      <div className="grid gap-2 md:grid-cols-2">
        {data.blockingIssues.length > 0 && (
          <IssueList
            title={t(
              "communicationChannels.whatsapp.flowOps.readiness.blockingIssues",
            )}
            items={data.blockingIssues}
            tone="destructive"
          />
        )}
        {data.warnings.length > 0 && (
          <IssueList
            title={t(
              "communicationChannels.whatsapp.flowOps.readiness.warnings",
            )}
            items={data.warnings}
            tone="warning"
          />
        )}
      </div>
    )}

    <div className="grid gap-2 md:grid-cols-2">
      {data.checks.map((check) => (
        <ReadinessCheckRow key={check.key} check={check} t={t} />
      ))}
    </div>
  </div>
);

interface IssueListProps {
  title: string;
  items: string[];
  tone: "destructive" | "warning";
}

const IssueList: React.FC<IssueListProps> = ({ title, items, tone }) => (
  <div className="flex min-w-0 flex-col gap-1">
    <span
      className={
        tone === "destructive"
          ? "text-xs font-medium text-destructive"
          : "text-xs font-medium text-foreground"
      }
    >
      {title}
    </span>
    <ul className="flex flex-col gap-1">
      {items.slice(0, 3).map((item) => (
        <li
          key={item}
          className="line-clamp-2 text-xs text-muted-foreground"
          title={item}
        >
          {item}
        </li>
      ))}
    </ul>
  </div>
);

interface ReadinessCheckRowProps {
  check: WhatsAppBotReadinessCheck;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const ReadinessCheckRow: React.FC<ReadinessCheckRowProps> = ({ check, t }) => {
  const Icon = readinessCheckIcon(check.status);
  return (
    <div className="flex min-w-0 items-start gap-2 rounded-md border p-2">
      <Icon className={readinessIconClass(check.status)} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-foreground">
            {t(
              `communicationChannels.whatsapp.flowOps.readiness.check.${check.key}`,
            )}
          </span>
          <Badge variant={readinessCheckVariant(check.status)}>
            {t(
              `communicationChannels.whatsapp.flowOps.readiness.checkStatus.${check.status}`,
            )}
          </Badge>
        </div>
        <span
          className="line-clamp-2 text-xs text-muted-foreground"
          title={check.message}
        >
          {check.message}
        </span>
      </div>
    </div>
  );
};

interface FlowStatusContentProps {
  data: WhatsAppFlowOperationsStatus;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const FlowStatusContent: React.FC<FlowStatusContentProps> = ({ data, t }) => (
  <div className="flex flex-col gap-4">
    <div className="grid gap-3 md:grid-cols-2">
      <InfoLine
        label={t("communicationChannels.whatsapp.flowOps.endpoint")}
        value={
          data.endpointUri ?? t("communicationChannels.whatsapp.flowOps.empty")
        }
      />
      <InfoLine
        label={t("communicationChannels.whatsapp.flowOps.action")}
        value={data.flowAction}
      />
    </div>

    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-foreground">
        {t("communicationChannels.whatsapp.flowOps.encryption")}
      </span>
      <div className="flex flex-wrap gap-2">
        <BooleanBadge
          ready={data.encryption.publicKeyConfiguredInEnv}
          label={t("communicationChannels.whatsapp.flowOps.keys.publicEnv")}
        />
        <BooleanBadge
          ready={data.encryption.privateKeyConfiguredInEnv}
          label={t("communicationChannels.whatsapp.flowOps.keys.privateEnv")}
        />
        <BooleanBadge
          ready={data.encryption.metaPublicKeyConfigured}
          label={t("communicationChannels.whatsapp.flowOps.keys.meta")}
        />
        {data.encryption.metaSignatureStatus && (
          <Badge variant="new_outline">
            {data.encryption.metaSignatureStatus}
          </Badge>
        )}
      </div>
      {data.encryption.error && (
        <p
          className="line-clamp-2 text-xs text-destructive"
          title={data.encryption.error}
        >
          {data.encryption.error}
        </p>
      )}
    </div>

    <div className="grid gap-2">
      {data.flows.map((flow) => (
        <FlowRow key={flow.key} flow={flow} t={t} />
      ))}
    </div>
  </div>
);

interface InfoLineProps {
  label: string;
  value: string;
}

const InfoLine: React.FC<InfoLineProps> = ({ label, value }) => (
  <div className="flex min-w-0 flex-col gap-1">
    <span className="text-xs font-medium text-foreground">{label}</span>
    <span
      className="truncate font-mono text-xs text-muted-foreground"
      title={value}
    >
      {value}
    </span>
  </div>
);

interface BooleanBadgeProps {
  ready: boolean;
  label: string;
}

const BooleanBadge: React.FC<BooleanBadgeProps> = ({ ready, label }) => (
  <Badge variant={ready ? "success" : "new_secondary"}>{label}</Badge>
);

interface FlowRowProps {
  flow: WhatsAppSystemFlowStatusItem;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const FlowRow: React.FC<FlowRowProps> = ({ flow, t }) => {
  const previewUrl = flow.previewUrl ?? undefined;
  const details =
    flow.flowName ??
    flow.flowId ??
    t("communicationChannels.whatsapp.flowOps.empty");
  const validationError = stringifyValidationErrors(flow.validationErrors);

  return (
    <div className="grid gap-3 rounded-md border bg-background p-3 md:grid-cols-[minmax(0,1fr)_auto]">
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            {t(`communicationChannels.whatsapp.flowOps.flow.${flow.key}`)}
          </span>
          <Badge variant={flowStatusVariant(flow)}>
            {t(`communicationChannels.whatsapp.flowOps.status.${flow.status}`)}
          </Badge>
          {flow.flowStatus && (
            <Badge variant="new_outline">{flow.flowStatus}</Badge>
          )}
        </div>
        <span
          className="truncate font-mono text-xs text-muted-foreground"
          title={details}
        >
          {details}
        </span>
        {(flow.error || validationError) && (
          <p
            className="line-clamp-2 text-xs text-destructive"
            title={flow.error ?? validationError}
          >
            {flow.error ?? validationError}
          </p>
        )}
      </div>

      {previewUrl && (
        <Button
          type="button"
          variant="new_outline"
          size="sm"
          className="self-start"
          onClick={() =>
            window.open(previewUrl, "_blank", "noopener,noreferrer")
          }
        >
          <ExternalLink className="size-4" />
          {t("communicationChannels.whatsapp.flowOps.actions.preview")}
        </Button>
      )}
    </div>
  );
};

const stringifyValidationErrors = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
};

const flowStatusVariant = (
  flow: WhatsAppSystemFlowStatusItem,
): React.ComponentProps<typeof Badge>["variant"] => {
  if (flow.status === "READ_FAILED") return "new_destructive";
  if (flow.status === "MISSING") return "new_secondary";
  if (flow.flowStatus?.toUpperCase() === "PUBLISHED") return "success";
  return "new_default";
};

const readinessStatusVariant = (
  status: WhatsAppBotReadinessStatusResponse["status"],
): React.ComponentProps<typeof Badge>["variant"] => {
  if (status === "READY") return "success";
  if (status === "FAILED") return "new_destructive";
  return "new_default";
};

const readinessCheckVariant = (
  status: WhatsAppBotReadinessCheck["status"],
): React.ComponentProps<typeof Badge>["variant"] => {
  if (status === "OK") return "success";
  if (status === "FAILED") return "new_destructive";
  return "new_default";
};

const readinessCheckIcon = (status: WhatsAppBotReadinessCheck["status"]) => {
  if (status === "OK") return CheckCircle2;
  if (status === "FAILED") return XCircle;
  return AlertTriangle;
};

const readinessIconClass = (
  status: WhatsAppBotReadinessCheck["status"],
): string => {
  if (status === "OK") return "mt-0.5 size-4 shrink-0 text-emerald-600";
  if (status === "FAILED") return "mt-0.5 size-4 shrink-0 text-destructive";
  return "mt-0.5 size-4 shrink-0 text-amber-600";
};
